import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  reserverenRekeningGroepSoorten,
  RekeningGroepSoort,
} from '../../model/RekeningGroep';
import { useCustomContext } from '../../context/CustomContext';
import { useState } from 'react';
import { HevelReserveOverForm } from './HevelReserveOverForm';
import { SaldoDTO } from '../../model/Saldo';

interface PotjesTabelProps {
  selectedLabels: string[];
  selectedSaldoStatussen: NonNullable<SaldoDTO['saldoStatus']>[];
}

export const PotjesTabel: React.FC<PotjesTabelProps> = ({
  selectedLabels,
  selectedSaldoStatussen,
}) => {
  const {
    rekeningGroepPerBetalingsSoort,
    stand,
    gekozenPeriode,
  } = useCustomContext();

  const [overTeHevelenReserve, setOverTeHevelenReserve] = useState<
    SaldoDTO | undefined
  >(undefined);

  const handleHevelReserveOverClick = (saldo: SaldoDTO) => {
    console.log(
      'handleHevelReserveOverClick',
      saldo.rekeningNaam,
      stand?.resultaatOpDatum.find(
        (item) => item.rekeningGroepNaam === saldo.rekeningNaam,
      ),
    );
    setOverTeHevelenReserve(saldo);
  };

  const formatAmount = (amount: number): string => {
    if (!amount) amount = 0;
    return amount.toLocaleString('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const isHuidigePeriode = gekozenPeriode?.periodeStatus === 'HUIDIG';

  return (
    <>
      {rekeningGroepPerBetalingsSoort.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{ maxWidth: 'xl', m: 'auto', mt: '10px' }}
        >
          <Table sx={{ width: '100%' }} aria-label="simple table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#888' }}>
                <TableCell sx={{ color: '#fff', padding: '15px' }}></TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  betaaldag
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  budget
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  opening
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  reservering
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  betalingen
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  reserve nu
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  nog nodig
                </TableCell>
                <TableCell align="right" sx={{ color: '#fff', padding: '5px' }}>
                  eindreserve
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {stand &&
                  stand.resultaatOpDatum
                    .filter((saldo) =>
                      reserverenRekeningGroepSoorten.includes(
                        saldo.rekeningGroepSoort as RekeningGroepSoort,
                      ),
                    )
                    .filter((saldo) =>
                      selectedLabels.length > 0
                        ? selectedLabels.every((label) =>
                            saldo.labels?.includes(label),
                          )
                        : true,
                    )
                    .filter((saldo) =>
                      selectedSaldoStatussen.length > 0
                        ? saldo.saldoStatus !== undefined &&
                          selectedSaldoStatussen.includes(saldo.saldoStatus)
                        : true,
                    )
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .reduce<{
                      rows: React.ReactNode[];
                      lastGroep?: string;
                    }>(
                      (acc, saldo, index) => {
                        if (saldo.rekeningGroepNaam !== acc.lastGroep) {
                          acc.rows.push(
                            <TableRow
                              key={`groep-${saldo.rekeningGroepNaam}-${index}`}
                            >
                              <TableCell
                                colSpan={10}
                                sx={{
                                  backgroundColor: '#f5f5f5',
                                  fontWeight: 'bold',
                                  padding: '5px',
                                }}
                              >
                                {saldo.rekeningGroepNaam}
                              </TableCell>
                            </TableRow>,
                          );
                          acc.lastGroep = saldo.rekeningGroepNaam;
                        }
                        acc.rows.push(
                          <TableRow key={saldo.rekeningNaam + index}>
                            <TableCell sx={{ padding: '5px' }}>
                              {saldo.rekeningNaam}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {saldo.budgetBetaalDag}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {formatAmount(saldo.budgetMaandBedrag)}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {formatAmount(saldo.openingsReserveSaldo)}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {formatAmount(saldo.periodeReservering)}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {formatAmount(saldo.periodeBetaling)}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {formatAmount(
                                saldo.openingsReserveSaldo +
                                  saldo.periodeReservering -
                                  saldo.periodeBetaling,
                              )}
                              {isHuidigePeriode ? (
                                <Button
                                  onClick={() =>
                                    handleHevelReserveOverClick(
                                      saldo,
                                    )
                                  }
                                  sx={{
                                    minWidth: '24px',
                                    color: 'grey',
                                    p: '5px',
                                  }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </Button>
                              ) : (
                                ''
                              )}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {formatAmount(saldo.komtNogNodig)}
                            </TableCell>
                            <TableCell sx={{ padding: '5px' }} align="right">
                              {formatAmount(
                                saldo.openingsReserveSaldo +
                                  saldo.periodeReservering -
                                  saldo.periodeBetaling -
                                  saldo.komtNogNodig,
                              )}
                            </TableCell>
                          </TableRow>,
                        );
                        return acc;
                      },
                      { rows: [], lastGroep: undefined },
                    ).rows}
              </>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {overTeHevelenReserve !== undefined && stand !== undefined && (
        <HevelReserveOverForm
          stand={stand}
          saldo={overTeHevelenReserve}
          onHevelReserveOverClose={() => setOverTeHevelenReserve(undefined)}
        />
      )}
    </>
  );
};
